import { useEffect, useId, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { enUS, ru as ruLocale } from "date-fns/locale";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { formControlChromeClassName } from "@/components/ui/form-control";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  addDateOnlyDays,
  addDateOnlyMonths,
  buildDateOnlyMonth,
  dateOnlyMonthStart,
  formatDateOnlyDisplay,
  parseDateOnly,
  startOfDateOnlyWeek,
  todayDateOnly,
  type DateOnlyLocale,
} from "@/lib/date-only";
import { cn } from "@/lib/utils";

const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;

export interface DateFieldProps {
  value: string | null;
  onChange: (value: string | null) => void;
  label: string;
  id?: string;
  name?: string;
  min?: string;
  max?: string;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  isDateDisabled?: (value: string) => boolean;
  className?: string;
  triggerClassName?: string;
  ref?: React.Ref<HTMLButtonElement>;
  "aria-invalid"?: React.AriaAttributes["aria-invalid"];
  "aria-describedby"?: string;
  "data-testid"?: string;
}

function resolveLocale(language?: string): DateOnlyLocale {
  return language?.startsWith("en") ? "en" : "ru";
}

export function DateField({
  value,
  onChange,
  label,
  id,
  name,
  min,
  max,
  required = false,
  disabled = false,
  readOnly = false,
  isDateDisabled,
  className,
  triggerClassName,
  ref,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
  "data-testid": testId = "date-field",
}: DateFieldProps): React.ReactElement {
  const { t, i18n } = useTranslation();
  const locale = resolveLocale(i18n.resolvedLanguage);
  const dateLocale = locale === "en" ? enUS : ruLocale;
  const initialDate = parseDateOnly(value ?? "") ? value! : todayDateOnly();
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => dateOnlyMonthStart(initialDate));
  const [focusedDate, setFocusedDate] = useState(initialDate);
  const dayRefs = useRef(new Map<string, HTMLButtonElement>());
  const autoId = useId();
  const labelId = id ? `${id}-label` : `${autoId}-label`;

  const days = useMemo(() => buildDateOnlyMonth(viewMonth), [viewMonth]);
  const minBoundary = min && parseDateOnly(min) ? min : null;
  const maxBoundary = max && parseDateOnly(max) ? max : null;
  const isUnavailable = (date: string): boolean =>
    (minBoundary !== null && date < minBoundary) ||
    (maxBoundary !== null && date > maxBoundary) ||
    (isDateDisabled?.(date) ?? false);

  const findAvailable = (start: string, direction: 1 | -1): string | null => {
    let candidate = start;
    for (let attempts = 0; attempts < 3660; attempts += 1) {
      if (
        (minBoundary !== null && candidate < minBoundary) ||
        (maxBoundary !== null && candidate > maxBoundary)
      ) {
        return null;
      }
      if (!isUnavailable(candidate)) return candidate;
      candidate = addDateOnlyDays(candidate, direction);
    }
    return null;
  };

  useEffect(() => {
    if (open) dayRefs.current.get(focusedDate)?.focus();
  }, [focusedDate, open, days]);

  const updateOpen = (nextOpen: boolean): void => {
    if (disabled || readOnly) return;
    if (nextOpen) {
      const preferred = value && !isUnavailable(value) ? value : todayDateOnly();
      const nextFocus = !isUnavailable(preferred)
        ? preferred
        : minBoundary && preferred < minBoundary
          ? (findAvailable(minBoundary, 1) ?? preferred)
          : maxBoundary && preferred > maxBoundary
            ? (findAvailable(maxBoundary, -1) ?? preferred)
            : (days.find((day) => !isUnavailable(day)) ?? preferred);
      setViewMonth(dateOnlyMonthStart(nextFocus));
      setFocusedDate(nextFocus);
    }
    setOpen(nextOpen);
  };

  const focusDate = (candidate: string, direction: 1 | -1): void => {
    const next = findAvailable(candidate, direction);
    if (!next) return;
    setViewMonth(dateOnlyMonthStart(next));
    setFocusedDate(next);
  };

  const selectDate = (date: string): void => {
    if (isUnavailable(date)) return;
    onChange(date);
    setOpen(false);
  };

  const handleDayKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>): void => {
    let candidate: string;
    let direction: 1 | -1 = 1;
    switch (event.key) {
      case "ArrowRight":
        candidate = addDateOnlyDays(focusedDate, 1);
        break;
      case "ArrowLeft":
        candidate = addDateOnlyDays(focusedDate, -1);
        direction = -1;
        break;
      case "ArrowDown":
        candidate = addDateOnlyDays(focusedDate, 7);
        break;
      case "ArrowUp":
        candidate = addDateOnlyDays(focusedDate, -7);
        direction = -1;
        break;
      case "Home":
        candidate = startOfDateOnlyWeek(focusedDate);
        break;
      case "End":
        candidate = addDateOnlyDays(startOfDateOnlyWeek(focusedDate), 6);
        direction = -1;
        break;
      case "PageUp":
        candidate = addDateOnlyMonths(focusedDate, -1);
        direction = -1;
        break;
      case "PageDown":
        candidate = addDateOnlyMonths(focusedDate, 1);
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        selectDate(focusedDate);
        return;
      default:
        return;
    }
    event.preventDefault();
    focusDate(candidate, direction);
  };

  const shiftMonth = (amount: 1 | -1): void => {
    const nextMonth = addDateOnlyMonths(viewMonth, amount);
    const nextDays = buildDateOnlyMonth(nextMonth);
    const nextFocus = nextDays.find(
      (day) => dateOnlyMonthStart(day) === dateOnlyMonthStart(nextMonth) && !isUnavailable(day),
    );
    setViewMonth(dateOnlyMonthStart(nextMonth));
    if (nextFocus) setFocusedDate(nextFocus);
  };

  const viewDate = parseDateOnly(viewMonth)!;
  const currentMonth = viewDate.getMonth();

  return (
    <div className={cn("relative inline-flex flex-col gap-1", className)}>
      <span id={labelId} className="sr-only">
        {label}
      </span>
      {name && <input type="hidden" name={name} value={value ?? ""} required={required} readOnly />}
      <Popover open={open} onOpenChange={updateOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            id={id}
            type="button"
            variant="outline"
            size="default"
            disabled={disabled}
            aria-readonly={readOnly || undefined}
            aria-required={required || undefined}
            aria-labelledby={labelId}
            aria-invalid={ariaInvalid}
            aria-describedby={ariaDescribedBy}
            data-testid={testId}
            className={cn(
              formControlChromeClassName,
              "min-w-40 justify-between font-normal",
              triggerClassName,
            )}
          >
            <span className={cn("tabular-nums", !value && "text-[var(--fg-4)]")}>
              {value ? formatDateOnlyDisplay(value, locale) : t("common.dateField.placeholder")}
            </span>
            <CalendarDays aria-hidden="true" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-auto rounded-[var(--radius-card)] border-[color:var(--rule-line)] p-3 shadow-none"
          style={{ boxShadow: "var(--shadow-plate)" }}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            dayRefs.current.get(focusedDate)?.focus();
          }}
        >
          <div className="mb-2 flex items-center justify-between gap-2">
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              aria-label={t("common.dateField.previousMonth")}
              data-testid={`${testId}-previous-month`}
              onClick={() => shiftMonth(-1)}
            >
              <ChevronLeft aria-hidden="true" />
            </Button>
            <span
              className="min-w-32 text-center font-condensed text-sm font-semibold capitalize"
              data-testid={`${testId}-month-label`}
              aria-live="polite"
            >
              {format(viewDate, "LLLL yyyy", { locale: dateLocale })}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              aria-label={t("common.dateField.nextMonth")}
              data-testid={`${testId}-next-month`}
              onClick={() => shiftMonth(1)}
            >
              <ChevronRight aria-hidden="true" />
            </Button>
          </div>

          <table role="grid" aria-labelledby={labelId} className="border-separate border-spacing-0">
            <thead>
              <tr>
                {WEEKDAYS.map((weekday) => (
                  <th
                    key={weekday}
                    scope="col"
                    className="size-7 text-center font-condensed text-xs font-medium text-[var(--fg-3)]"
                  >
                    {t(`common.dateField.weekdays.${weekday}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 6 }, (_, rowIndex) => (
                <tr key={days[rowIndex * 7]}>
                  {days.slice(rowIndex * 7, rowIndex * 7 + 7).map((day) => {
                    const parsed = parseDateOnly(day)!;
                    const unavailable = isUnavailable(day);
                    const selected = day === value;
                    const today = day === todayDateOnly();
                    const inMonth = parsed.getMonth() === currentMonth;
                    const ariaLabel = format(
                      parsed,
                      locale === "en" ? "MMMM d, yyyy" : "d MMMM yyyy",
                      { locale: dateLocale },
                    );
                    return (
                      <td key={day} role="gridcell" aria-selected={selected} className="p-0">
                        <Button
                          ref={(node) => {
                            if (node) dayRefs.current.set(day, node);
                            else dayRefs.current.delete(day);
                          }}
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          tabIndex={focusedDate === day && !unavailable ? 0 : -1}
                          disabled={unavailable}
                          aria-label={ariaLabel}
                          data-testid={`${testId}-day-${day}`}
                          className={cn(
                            "font-mono text-xs tabular-nums",
                            !inMonth && "text-[var(--fg-4)]",
                            today && "ring-1 ring-[var(--rule-strong)]",
                            selected &&
                              "bg-[var(--fg-1)] text-[var(--fg-inverse)] hover:bg-[var(--fg-1)] hover:text-[var(--fg-inverse)]",
                          )}
                          onClick={() => selectDate(day)}
                          onKeyDown={handleDayKeyDown}
                        >
                          {parsed.getDate()}
                        </Button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>

          {value && !required && (
            <div className="mt-2 flex justify-end border-t border-[color:var(--rule-faint)] pt-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                data-testid={`${testId}-clear`}
                onClick={() => {
                  onChange(null);
                  setOpen(false);
                }}
              >
                {t("common.dateField.clear")}
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
